import { precipTimeRange } from "@divespots/contracts";
import type { PrecipField, WindField } from "@divespots/contracts";
import { useMemo, useState } from "react";
import { HOUR_MS, SLIDER_STEP_MS, clampTime, defaultTime, sliderRange } from "./time.ts";
import type { TimeRange } from "./time.ts";

export interface WindTime {
  /** Forecast time in epoch ms; null until a field is there. */
  t: number | null;
  range: TimeRange | null;
  /** The default step (the current hour): the only one labelled "Jetzt" (wind). */
  defaultT: number | null;
  /** "Now" when the field arrived: the default hour and the "Jetzt" label key off this. */
  now: number;
  /** Slider step: half hours with wind (interpolated), whole hours for precipitation alone. */
  step: number;
  setT: (t: number) => void;
}

interface Fields {
  windOn: boolean;
  wind: WindField | undefined;
  precipOn: boolean;
  precip: PrecipField | undefined;
}

/**
 * The shared slider time of the overlays: the current hour until the user moves it, always inside
 * the range. With wind on the range is the wind field's (precipitation shows "no data" at steps
 * without a sum); with precipitation alone it is `precipTimeRange` and the wind field is never
 * needed.
 */
export function useWindTime({ windOn, wind, precipOn, precip }: Fields): WindTime {
  const [picked, setPicked] = useState<number | null>(null);
  const field = windOn ? wind : precipOn ? precip : undefined;
  const now = useMemo(() => Date.now(), [field]);
  const range = useMemo<TimeRange | null>(() => {
    if (windOn) return wind ? sliderRange(wind.times, now) : null;
    if (!precipOn || !precip) return null;
    const r = precipTimeRange(precip.times);
    return r ? { min: r.startMs, max: r.endMs } : null;
  }, [windOn, wind, precipOn, precip, now]);
  const step = windOn ? SLIDER_STEP_MS : HOUR_MS;
  const whole = (v: number) => (windOn ? v : Math.floor(v / HOUR_MS) * HOUR_MS);
  const defaultT = range ? defaultTime(range, now) : null;
  const t = range
    ? picked === null
      ? defaultTime(range, now)
      : whole(clampTime(range, picked))
    : null;
  return { t, range, defaultT, now, step, setT: setPicked };
}
